/**
 * Whether a fixed-term/session-based program has already ended.
 *
 * Used by the public catalog, enrollment page, and enrollment API to keep
 * parents from enrolling in a class whose end date has passed. Recurring
 * (non-session-based) programs return false — they have no fixed end and
 * stay open until the gym manually closes enrollment.
 *
 * Accepts either `end_date` (catalog/browse API shape) or `session_end_date`
 * (raw `classes` table shape) — different layers of the codebase use
 * different field names for the same concept.
 *
 * `gymToday` is the gym's own calendar day, 'YYYY-MM-DD' (getOrgTimezone →
 * getGymNowParts(tz).dateStr). Never the server's clock: the server runs in
 * UTC, which is already tomorrow by late afternoon in a US gym. The class is
 * ended the day after its last day, so the last day itself stays open.
 */
import { getGymNowParts } from '@/utils/programs/nextOpenGymSession';

function endedBefore(endDate: string, gymToday: string): boolean {
  return endDate.slice(0, 10) < gymToday;
}

export function isProgramEnded(
  program: {
    is_session_based?: boolean | null;
    end_date?: string | null;
    session_end_date?: string | null;
  },
  gymToday: string,
): boolean {
  if (!program.is_session_based) return false;
  const endDateStr = program.end_date || program.session_end_date;
  if (!endDateStr) return false;
  return endedBefore(endDateStr, gymToday);
}

/**
 * Whether a class section has already ended.
 *
 * The class row is the source of truth for session dates — class_sections has
 * its own session_end_date column that's effectively a denormalized copy. When
 * the two disagree (e.g. a class extension didn't propagate to its sections),
 * trust the class. Pass `programEndDate` to enable this fallback; without it,
 * we read whatever is on the section row.
 *
 * Used by the enrollment page (gray out past sections) and the enrollment API
 * (defense-in-depth). `gymToday` as for isProgramEnded.
 */
export function isSectionPast(
  section: { session_end_date?: string | null },
  programEndDate: string | null | undefined,
  gymToday: string,
): boolean {
  const endDateStr = programEndDate ?? section.session_end_date;
  if (!endDateStr) return false;
  return endedBefore(endDateStr, gymToday);
}

/**
 * Gym-local today for a program from the public detail API, for the two calls
 * above. `gym_timezone` is the enroll API's clock; `timezone` is the older
 * field, read only if a response lacks the first.
 */
export function publicProgramGymToday(
  program: { gym_timezone?: string | null; timezone?: string | null } | null | undefined,
): string {
  return getGymNowParts(program?.gym_timezone || program?.timezone).dateStr;
}
